import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { KeyRound, CheckCircle2 } from "lucide-react";

const ROLE_LABELS = {
    super_admin: "Super-admin",
    tenant_admin: "Beheerder",
    member: "Medewerker",
};

export default function Profile() {
    const { user, tenant } = useAuth();
    const [form, setForm] = useState({
        current_password: "",
        new_password: "",
        confirm_password: "",
    });
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess(false);
        if (!form.current_password || !form.new_password || !form.confirm_password) {
            setError("Vul alle velden in");
            return;
        }
        if (form.new_password !== form.confirm_password) {
            setError("De nieuwe wachtwoorden komen niet overeen");
            return;
        }
        if (form.new_password.length < 8) {
            setError("Nieuw wachtwoord moet minimaal 8 tekens lang zijn");
            return;
        }
        if (form.new_password === form.current_password) {
            setError("Nieuw wachtwoord moet anders zijn dan het huidige");
            return;
        }
        setSaving(true);
        try {
            await api.post("/auth/change-password", {
                current_password: form.current_password,
                new_password: form.new_password,
            });
            setSuccess(true);
            setForm({ current_password: "", new_password: "", confirm_password: "" });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-8 lg:p-12 max-w-3xl mx-auto" data-testid="profile-page">
            <PageHeader
                kicker="Mijn account"
                title="Profiel & wachtwoord"
                subtitle="Beheer je accountgegevens en wachtwoord."
                testid="profile-header"
            />

            <section className="bg-white border border-border rounded-md p-6 mb-8" data-testid="profile-info">
                <h2 className="text-lg font-semibold text-ink-900 mb-4">Accountgegevens</h2>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                    <Row label="Naam" value={user?.name || "—"} />
                    <Row label="E-mail" value={user?.email || "—"} mono />
                    <Row label="Rol" value={ROLE_LABELS[user?.role] || user?.role} />
                    <Row label="Tenant" value={tenant?.name || "—"} />
                </dl>
            </section>

            <section className="bg-white border border-border rounded-md p-6" data-testid="change-password-section">
                <h2 className="text-lg font-semibold text-ink-900 mb-2 flex items-center gap-2">
                    <KeyRound className="h-5 w-5 text-florefy-900" /> Wachtwoord wijzigen
                </h2>
                <p className="text-sm text-ink-500 mb-5">
                    Voer eerst je huidige wachtwoord in en daarna twee keer het nieuwe. Minimaal 8 tekens.
                </p>
                {success && (
                    <div className="mb-4 flex items-center gap-2 text-sm bg-florefy-50 border border-florefy-200 text-florefy-900 px-3 py-2 rounded-sm" data-testid="password-success">
                        <CheckCircle2 className="h-4 w-4" />
                        Wachtwoord succesvol bijgewerkt. Bewaar het goed.
                    </div>
                )}
                {error && (
                    <div className="mb-4 text-sm bg-destructive/10 border border-destructive text-destructive px-3 py-2 rounded-sm" data-testid="password-error">
                        {error}
                    </div>
                )}
                <form onSubmit={submit} className="space-y-4">
                    <Field
                        label="Huidig wachtwoord"
                        value={form.current_password}
                        onChange={(v) => setForm({ ...form, current_password: v })}
                        autoComplete="current-password"
                        testid="current-password"
                    />
                    <Field
                        label="Nieuw wachtwoord (min. 8 tekens)"
                        value={form.new_password}
                        onChange={(v) => setForm({ ...form, new_password: v })}
                        autoComplete="new-password"
                        testid="new-password"
                    />
                    <Field
                        label="Bevestig nieuw wachtwoord"
                        value={form.confirm_password}
                        onChange={(v) => setForm({ ...form, confirm_password: v })}
                        autoComplete="new-password"
                        testid="confirm-password"
                    />
                    <div className="pt-2">
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-4 py-2 rounded-md bg-florefy-900 hover:bg-florefy-800 text-white text-sm font-medium disabled:opacity-60"
                            data-testid="btn-change-password"
                        >
                            {saving ? "Bezig met opslaan…" : "Wachtwoord opslaan"}
                        </button>
                    </div>
                </form>
            </section>
        </div>
    );
}

function Row({ label, value, mono }) {
    return (
        <div>
            <dt className="data-label">{label}</dt>
            <dd className={`mt-1 text-ink-900 ${mono ? "font-mono text-xs" : ""}`}>{value}</dd>
        </div>
    );
}

function Field({ label, value, onChange, autoComplete, testid }) {
    return (
        <div>
            <label className="block text-xs font-medium text-ink-700 mb-1">{label}</label>
            <input
                type="password"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                autoComplete={autoComplete}
                required
                className="w-full px-3 py-2 rounded-md border border-border text-sm focus:outline-none focus:ring-2 focus:ring-florefy-900 focus:border-florefy-900"
                data-testid={`input-${testid}`}
            />
        </div>
    );
}
